/**
 * Team Admin → custom stat field form (FR-STATS-01..04, FR-FERPA-ACK-02).
 *
 * The FERPA reminder at the top is **non-dismissible and verbatim**: there is
 * no close button, no timeout and no "don't show again". Its text comes from
 * `FERPA_CUSTOM_STAT_REMINDER_TEXT` and is asserted character-for-character by
 * `custom-stat-banner.smoke.test.tsx` (merge-blocking). The element carrying
 * `data-testid="ferpa-reminder-banner"` holds ONLY that sentence — the heading
 * and the warning glyph are siblings/CSS, so `textContent` stays exact.
 *
 * The component is presentational: it owns draft state and inline validation,
 * and reports outcomes through `onSubmit`. Server rejections are mapped back
 * onto the offending control via `ApiError.field`, with 402
 * `TIER_LIMIT_EXCEEDED` surfaced as an upgrade panel rather than a form error.
 */
import { useId, useMemo, useState } from 'react';

import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import {
  STAT_FIELD_APPLIES_TO,
  STAT_FIELD_DATA_TYPES,
  type StatFieldCapacity,
  type StatFieldDefinition,
  type StatFieldEnumOption,
  type StatFieldInput,
  type StatFieldPatch,
} from '../../lib/api-types';
import { FERPA_CUSTOM_STAT_REMINDER_TEXT } from '../../lib/constants';

import {
  APPLIES_TO_LABELS,
  DATA_TYPE_HINTS,
  DATA_TYPE_LABELS,
  createEmptyCustomStatDraft,
  hasDraftErrors,
  suggestStatKey,
  toCustomStatDraft,
  toStatFieldInput,
  toStatFieldPatch,
  validateCustomStatDraft,
  type CustomStatDraft,
  type CustomStatDraftErrors,
} from './custom-stat-form-state';

export interface EnumCategoryChoice {
  category: string;
  values: StatFieldEnumOption[];
}

export interface CustomStatFormProps {
  /** Existing field → edit mode: `stat_key` and `data_type` are locked. */
  field?: StatFieldDefinition | null;
  /** Option lists a `Pick from a list` field can draw from. */
  enumCategories?: readonly EnumCategoryChoice[];
  /** Tier headroom. `remaining === 0` blocks submit with an upgrade CTA. */
  capacity?: StatFieldCapacity | null;
  /** Where the upgrade CTA points. */
  upgradeHref?: string;
  onSubmitCreate?: (input: StatFieldInput) => Promise<unknown>;
  onSubmitEdit?: (patch: StatFieldPatch) => Promise<unknown>;
  onCancel?: () => void;
}

const FIELD_CODE_HINT = 'Machine name (snake_case). Cannot be changed once the field exists.';

export function CustomStatForm({
  field = null,
  enumCategories = [],
  capacity = null,
  upgradeHref = '/app/settings',
  onSubmitCreate,
  onSubmitEdit,
  onCancel,
}: CustomStatFormProps): JSX.Element {
  const isEditing = field !== null;
  const formId = useId();
  const [draft, setDraft] = useState<CustomStatDraft>(() =>
    field === null ? createEmptyCustomStatDraft() : toCustomStatDraft(field)
  );
  /** What the form opened with — dirtiness is any deviation from this. */
  const [initialDraft] = useState<CustomStatDraft>(draft);
  const [isKeyEdited, setIsKeyEdited] = useState(isEditing);
  const [serverErrors, setServerErrors] = useState<CustomStatDraftErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [tierMessage, setTierMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const availableCategories = useMemo(
    () => enumCategories.map((choice) => choice.category),
    [enumCategories]
  );
  const clientErrors = validateCustomStatDraft(draft, { isEditing, availableCategories });
  const errors: CustomStatDraftErrors = { ...serverErrors, ...(isSubmitted ? clientErrors : {}) };
  const isAtLimit = !isEditing && capacity !== null && capacity.remaining === 0;

  function patch(next: Partial<CustomStatDraft>): void {
    setServerErrors({});
    setFormError(null);
    setDraft((current) => ({ ...current, ...next }));
  }

  function changeLabel(value: string): void {
    patch(isKeyEdited ? { label: value } : { label: value, statKey: suggestStatKey(value) });
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setIsSubmitted(true);
    setFormError(null);
    setTierMessage(null);
    if (hasDraftErrors(clientErrors) || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (isEditing) await onSubmitEdit?.(toStatFieldPatch(draft));
      else await onSubmitCreate?.(toStatFieldInput(draft));
      setServerErrors({});
    } catch (caught) {
      if (ApiError.isApiError(caught) && caught.status === 402) {
        setTierMessage(caught.message);
      } else if (ApiError.isApiError(caught)) {
        const key = SERVER_FIELD_MAP[caught.field ?? ''];
        if (key === undefined) setFormError(caught.message);
        else setServerErrors({ [key]: caught.message });
      } else {
        setFormError('The field could not be saved. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const selectedCategory = enumCategories.find((choice) => choice.category === draft.enumCategory);

  return (
    <form className="stat-field-form stack" onSubmit={handleSubmit} noValidate>
      <UnsavedChangesGuard
        isDirty={!isSubmitting && JSON.stringify(draft) !== JSON.stringify(initialDraft)}
      />
      {/* FR-FERPA-ACK-02 — non-dismissible, verbatim. Do not add child nodes to
          the testid element: the smoke test compares its textContent exactly. */}
      <aside className="ferpa-banner" role="note" aria-labelledby={`${formId}-ferpa`}>
        <h3 className="ferpa-banner__title" id={`${formId}-ferpa`}>
          FERPA reminder
        </h3>
        <p className="ferpa-banner__text" data-testid="ferpa-reminder-banner">
          {FERPA_CUSTOM_STAT_REMINDER_TEXT}
        </p>
      </aside>

      {isAtLimit ? (
        <div className="notice notice--warning" role="status">
          <p>
            You&rsquo;ve reached the custom field limit for your tier
            {capacity?.limit === null ? '' : ` (${String(capacity?.limit ?? 0)} fields)`}. Retire a
            field to make room, or upgrade for more.
          </p>
          <a className="btn btn--primary btn--sm" href={upgradeHref}>
            View plans
          </a>
        </div>
      ) : null}

      {tierMessage === null ? null : (
        <div className="notice notice--warning" role="alert" data-testid="tier-limit-notice">
          <p>{tierMessage}</p>
          <a className="btn btn--primary btn--sm" href={upgradeHref}>
            View plans
          </a>
        </div>
      )}

      {formError === null ? null : (
        <p className="form-error" role="alert">
          {formError}
        </p>
      )}

      <div className="form-grid">
        <label className="field" htmlFor={`${formId}-label`}>
          <span className="field__label">
            Display Name<span aria-hidden="true"> *</span>
          </span>
          <input
            id={`${formId}-label`}
            className="field__control"
            value={draft.label}
            maxLength={200}
            autoComplete="off"
            aria-invalid={errors.label === undefined ? undefined : true}
            onChange={(event) => changeLabel(event.target.value)}
          />
          <span className="field__hint">The label coaches see on Play Entry and the archive.</span>
          {errors.label === undefined ? null : (
            <span className="field__error" role="alert">
              {errors.label}
            </span>
          )}
        </label>

        <label className="field" htmlFor={`${formId}-key`}>
          <span className="field__label">
            Field Code<span aria-hidden="true"> *</span>
          </span>
          <input
            id={`${formId}-key`}
            className="field__control"
            value={draft.statKey}
            maxLength={64}
            autoComplete="off"
            readOnly={isEditing}
            disabled={isEditing}
            aria-invalid={errors.statKey === undefined ? undefined : true}
            onChange={(event) => {
              setIsKeyEdited(true);
              patch({ statKey: event.target.value.toLowerCase() });
            }}
          />
          <span className="field__hint">{FIELD_CODE_HINT}</span>
          {errors.statKey === undefined ? null : (
            <span className="field__error" role="alert">
              {errors.statKey}
            </span>
          )}
        </label>

        <label className="field" htmlFor={`${formId}-type`}>
          <span className="field__label">Data Type</span>
          <select
            id={`${formId}-type`}
            className="field__control"
            value={draft.dataType}
            disabled={isEditing}
            onChange={(event) =>
              patch({ dataType: event.target.value as CustomStatDraft['dataType'] })
            }
          >
            {STAT_FIELD_DATA_TYPES.map((type) => (
              <option key={type} value={type}>
                {DATA_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          <span className="field__hint">
            {isEditing
              ? 'The data type is fixed once plays can reference the field.'
              : DATA_TYPE_HINTS[draft.dataType]}
          </span>
        </label>

        <label className="field" htmlFor={`${formId}-unit`}>
          <span className="field__label">Applies To</span>
          <select
            id={`${formId}-unit`}
            className="field__control"
            value={draft.appliesTo}
            onChange={(event) =>
              patch({ appliesTo: event.target.value as CustomStatDraft['appliesTo'] })
            }
          >
            {STAT_FIELD_APPLIES_TO.map((unit) => (
              <option key={unit} value={unit}>
                {APPLIES_TO_LABELS[unit]}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Which Play Entry toggle offers this field. &ldquo;All units&rdquo; shows it on every
            one.
          </span>
        </label>

        {draft.dataType === 'enum' ? (
          <label className="field" htmlFor={`${formId}-enum`}>
            <span className="field__label">
              Option List<span aria-hidden="true"> *</span>
            </span>
            <select
              id={`${formId}-enum`}
              className="field__control"
              value={draft.enumCategory}
              aria-invalid={errors.enumCategory === undefined ? undefined : true}
              onChange={(event) => patch({ enumCategory: event.target.value })}
            >
              <option value="">Choose an option list…</option>
              {enumCategories.map((choice) => (
                <option key={choice.category} value={choice.category}>
                  {choice.category} ({choice.values.length})
                </option>
              ))}
            </select>
            <span className="field__hint">
              {selectedCategory === undefined
                ? 'Options come from your team lists, so renaming one keeps old plays readable.'
                : `Options: ${selectedCategory.values.map((option) => option.label).join(', ')}`}
            </span>
            {errors.enumCategory === undefined ? null : (
              <span className="field__error" role="alert">
                {errors.enumCategory}
              </span>
            )}
          </label>
        ) : null}

        <label className="field" htmlFor={`${formId}-hudl`}>
          <span className="field__label">Hudl Export Column</span>
          <input
            id={`${formId}-hudl`}
            className="field__control"
            value={draft.hudlExportKey}
            maxLength={120}
            autoComplete="off"
            onChange={(event) => patch({ hudlExportKey: event.target.value })}
          />
          <span className="field__hint">
            Optional. Hudl CSV column name for export mapping; leave blank to exclude the field.
          </span>
        </label>
      </div>

      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={draft.isRequired}
          onChange={(event) => patch({ isRequired: event.target.checked })}
        />
        <span>Require a value on every play for this unit</span>
      </label>

      <div className="btn-row dialog-actions stat-field-form__actions">
        {capacity === null || capacity.limit === null ? null : (
          <span className="muted stat-field-form__capacity">
            {capacity.used} of {capacity.limit} custom fields used
          </span>
        )}
        {onCancel === undefined ? null : (
          <button type="button" className="btn btn--secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn--primary" disabled={isSubmitting || isAtLimit}>
          {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Custom Field'}
        </button>
      </div>
    </form>
  );
}

/** RFC 7807 `details.field` → draft key, so a 400 lands on the right control. */
const SERVER_FIELD_MAP: Record<string, keyof CustomStatDraft | undefined> = {
  stat_key: 'statKey',
  label: 'label',
  data_type: 'dataType',
  applies_to: 'appliesTo',
  enum_category: 'enumCategory',
  enum_values: 'enumCategory',
  hudl_export_key: 'hudlExportKey',
};
